import React, { useState, useEffect } from 'react';
import './App.css';
import Analytics from './Analytics';

function App() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId).scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="App">
      {/* Navigation Bar */}
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <span className="logo-text">SM Agency</span>
            </div>
            <ul className="nav-links">
              <li><button onClick={() => scrollToSection('home')} className="nav-link">Home</button></li>
              <li><button onClick={() => scrollToSection('about')} className="nav-link">About</button></li>
              <li><button onClick={() => scrollToSection('services')} className="nav-link">Services</button></li>
              <li><button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button></li>
            </ul>
            <div className="nav-cta">
              <button onClick={() => scrollToSection('contact')} className="cta-btn">Get Started</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <header className="header" id="home">
        <div className="header-background">
          <div className="gradient-overlay"></div>
          <div className="floating-elements">
            <div className="floating-icon" style={{top: '20%', left: '10%'}}>📱</div>
            <div className="floating-icon" style={{top: '30%', right: '15%'}}>🚀</div>
            <div className="floating-icon" style={{bottom: '25%', left: '20%'}}>💡</div>
            <div className="floating-icon" style={{bottom: '35%', right: '25%'}}>🎯</div>
          </div>
        </div>
        <div className="container">
          <div className="header-content">
            <div className="header-badge">✨ International Digital Agency</div>
            <h1 className="main-title">
              SM Agency <span className="highlight">LLC</span>
            </h1>
            <p className="main-subtitle">International Social Media & Talent Management Agency</p>
            <p className="header-description">
              We connect creators and brands through digital marketing, content strategy, and talent management.
            </p>
            <div className="header-cta">
              <button onClick={() => scrollToSection('services')} className="cta-primary">
                Our Services
                <span className="cta-arrow">→</span>
              </button>
              <button onClick={() => scrollToSection('about')} className="cta-secondary">
                Learn More
              </button>
            </div>
            <div className="header-stats">
              <div className="stat">
                <span className="stat-number">500+</span>
                <span className="stat-label">Creators Managed</span>
              </div>
              <div className="stat">
                <span className="stat-number">50+</span>
                <span className="stat-label">Brand Partners</span>
              </div>
              <div className="stat">
                <span className="stat-number">3</span>
                <span className="stat-label">Countries</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* About Section */}
      <section className="about" id="about">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">🌟 About Us</span>
            <h2 className="section-title">Who We Are</h2>
            <p className="section-subtitle">Building bridges between creators and global brands</p>
          </div>
          <div className="about-grid">
            <div className="about-content">
              <div className="about-card">
                <div className="card-icon">🏢</div>
                <h3>U.S. Registered Company</h3>
                <p>
                  SM Agency LLC is a U.S.-registered company that provides social media management, 
                  digital marketing, and talent coordination services.
                </p>
              </div>
              <div className="about-card">
                <div className="card-icon">🌍</div>
                <h3>Global Network</h3>
                <p>
                  We help independent creators and influencers build their online presence, connect 
                  with international brands, and manage their collaborations.
                </p>
              </div>
              <div className="about-card">
                <div className="card-icon">🤝</div>
                <h3>Strategic Partnerships</h3>
                <p>
                  Our team works closely with European partners such as IMAT92 Sagl (Switzerland), 
                  Innov8 Agency, and One Shot Agency (Italy) to coordinate marketing campaigns.
                </p>
              </div>
            </div>
            <div className="about-visual">
              <div className="stats-visual">
                <div className="stat-circle">
                  <span className="stat-big">98%</span>
                  <span className="stat-desc">Client Satisfaction</span>
                </div>
                <div className="partnership-logos">
                  <div className="logo-placeholder">🇺🇸 USA</div>
                  <div className="logo-placeholder">🇨🇭 Switzerland</div>
                  <div className="logo-placeholder">🇮🇹 Italy</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services" id="services">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">🚀 What We Do</span>
            <h2 className="section-title">Our Services</h2>
            <p className="section-subtitle">End-to-end solutions for digital success</p>
          </div>
          <div className="services-showcase">
            <div className="services-visual">
              <Analytics />
            </div>
            <div className="services-grid">
              <div className="service-card featured">
                <div className="service-header">
                  <div className="service-icon gradient">🎯</div>
                  <div className="service-badge">Most Popular</div>
                </div>
                <h3 className="service-title">Talent Management</h3>
                <p className="service-description">
                  We coordinate collaborations, sponsorships, and brand deals for our talent network.
                </p>
                <ul className="service-features">
                  <li>Brand Deal Negotiations</li>
                  <li>Partnership Coordination</li>
                  <li>Partnership Growth</li>
                </ul>
              </div>
              <div className="service-card">
                <div className="service-header">
                  <div className="service-icon">📱</div>
                </div>
                <h3 className="service-title">Social Media Marketing</h3>
                <p className="service-description">
                  We develop strategies to grow audiences across major platforms and maximize engagement.
                </p>
                <ul className="service-features">
                  <li>Multi-Platform Strategy</li>
                  <li>Audience Growth</li>
                  <li>Engagement Optimization</li>
                </ul>
              </div>
              <div className="service-card">
                <div className="service-header">
                  <div className="service-icon">📅</div>
                </div>
                <h3 className="service-title">Content Planning</h3>
                <p className="service-description">
                  We help optimize creators' online presence through strategic content planning.
                </p>
                <ul className="service-features">
                  <li>Content Calendar</li>
                  <li>Strategic Planning</li>
                  <li>Performance Tracking</li>
                </ul>
              </div>
              <div className="service-card">
                <div className="service-header">
                  <div className="service-icon">💼</div>
                </div>
                <h3 className="service-title">Partnership Operations</h3>
                <p className="service-description">
                  We handle international coordination and partnership management for seamless business operations.
                </p>
                <ul className="service-features">
                  <li>Global Coordination</li>
                  <li>Partnership Management</li>
                  <li>Performance Analytics</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact" id="contact">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">💬 Get In Touch</span>
            <h2 className="section-title">Let's Work Together</h2>
            <p className="section-subtitle">Ready to elevate your brand? We're here to help.</p>
          </div>
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-card">
                <div className="contact-header">
                  <div className="company-logo">SM</div>
                  <div>
                    <h3 className="company-name">SM Agency LLC</h3>
                    <p className="company-tagline">Your Global Digital Partner</p>
                  </div>
                </div>
                <div className="contact-details">
                  <div className="detail-item">
                    <div className="detail-icon">🏛️</div>
                    <div>
                      <p><strong>Registered:</strong> State of Wyoming, USA</p>
                      <p><strong>Business ID:</strong> 2023-001239267</p>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-icon">📧</div>
                    <div>
                      <p><strong>Email:</strong></p>
                      <a href="mailto:agency.smagency@gmail.com" className="email-link">agency.smagency@gmail.com</a>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-icon">🌍</div>
                    <div>
                      <p><strong>Global Partnerships:</strong></p>
                      <div className="partnerships">
                        <span className="partnership-tag">🇺🇸 United States</span>
                        <span className="partnership-tag">🇨🇭 Switzerland</span>
                        <span className="partnership-tag">🇮🇹 Italy</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="trust-indicators">
                  <div className="trust-item">
                    <span className="trust-icon">✅</span>
                    <span>Verified Business</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">🔒</span>
                    <span>Secure Transactions</span>
                  </div>
                  <div className="trust-item">
                    <span className="trust-icon">⚡</span>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form">
              <div className="form-header">
                <h3>Start Your Journey</h3>
                <p>Tell us about your project and let's create something amazing together.</p>
              </div>
              <form className="form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Your Name</label>
                    <input type="text" placeholder="John Doe" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Your Email</label>
                    <input type="email" placeholder="john@company.com" className="form-input" />
                  </div>
                </div>
                <div className="form-group">
                  <label>Company/Organization</label>
                  <input type="text" placeholder="Your Company Name" className="form-input" />
                </div>
                <div className="form-group">
                  <label>Project Type</label>
                  <select className="form-input">
                    <option>Select a service</option>
                    <option>Talent Management</option>
                    <option>Social Media Marketing</option>
                    <option>Content Planning</option>
                    <option>Partnership Operations</option>
                    <option>Custom Solution</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Tell us about your project</label>
                  <textarea placeholder="Describe your goals, timeline, and what success looks like to you..." className="form-textarea" rows="5"></textarea>
                </div>
                <button type="submit" className="submit-btn gradient-btn">
                  <span>Send Message</span>
                  <span className="btn-arrow">→</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <p>&copy; 2025 SM Agency LLC. All rights reserved.</p>
            <p className="privacy-notice">
              This website provides information about SM Agency LLC's consulting and marketing services.
              We do not host or distribute user-generated content on this website.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;